import { manufacturingFetch } from "@/lib/manufacturingClient";
import { requireSupabase } from "@/lib/supabase";

type SignOk = {
  ok: true;
  bucket: string;
  path: string;
  token: string;
  signed_url: string;
  attachment_id: string;
  content_type: string;
};

type CommitOk = {
  ok: true;
  public_url: string;
};

function isSignOk(x: unknown): x is SignOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return (
    o.ok === true &&
    typeof o.bucket === "string" &&
    typeof o.path === "string" &&
    typeof o.token === "string" &&
    typeof o.attachment_id === "string" &&
    typeof o.content_type === "string"
  );
}

function isCommitOk(x: unknown): x is CommitOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return o.ok === true && typeof o.public_url === "string";
}

export type ServiceEvidenceUploadResult =
  | { ok: true; public_url: string }
  | { ok: false; error: string };

export async function uploadServiceCheckInEvidence(
  eventId: string,
  file: File
): Promise<ServiceEvidenceUploadResult> {
  const sb = requireSupabase();
  const contentType = (file.type || "application/octet-stream").toLowerCase();

  const sign = await manufacturingFetch<unknown>("/api/service/check-ins/evidence", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      op: "sign",
      event_id: eventId,
      content_type: contentType,
      byte_size: file.size,
    }),
  });
  if (!sign.ok) return { ok: false, error: sign.error };
  if (!isSignOk(sign.data)) {
    const errCandidate = (sign.data as { error?: unknown })?.error;
    return {
      ok: false,
      error: typeof errCandidate === "string" ? errCandidate : "Sign response was malformed.",
    };
  }
  const s = sign.data;

  const { error: upErr } = await sb.storage.from(s.bucket).uploadToSignedUrl(
    s.path,
    s.token,
    file,
    { contentType: s.content_type, upsert: false }
  );
  if (upErr) return { ok: false, error: upErr.message };

  const commit = await manufacturingFetch<unknown>("/api/service/check-ins/evidence", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      op: "commit",
      event_id: eventId,
      attachment_id: s.attachment_id,
      object_path: s.path,
      content_type: s.content_type,
    }),
  });
  if (!commit.ok) return { ok: false, error: commit.error };
  if (!isCommitOk(commit.data)) {
    const errCandidate = (commit.data as { error?: unknown })?.error;
    return {
      ok: false,
      error: typeof errCandidate === "string" ? errCandidate : "Commit response was malformed.",
    };
  }
  return { ok: true, public_url: commit.data.public_url };
}
